import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X } from 'lucide-react';

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = url;
  });
}

async function getCroppedImg(imageSrc, pixelCrop, circular = false) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const size = Math.max(pixelCrop.width, pixelCrop.height);
  canvas.width = circular ? size : pixelCrop.width;
  canvas.height = circular ? size : pixelCrop.height;

  if (circular) {
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.clip();
  }

  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, canvas.width, canvas.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
  });
}

export default function ImageCropper({ imageSrc, onSave, onCancel, aspect = 1, circular = false }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, circular);
      const file = new File([croppedBlob], 'cropped.jpg', { type: 'image/jpeg' });
      await onSave(file);
    } catch (err) {
      console.error('Crop error:', err);
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-[10001] bg-[#111111] flex flex-col overflow-hidden"
      data-testid="image-cropper"
      style={{ height: '100dvh', width: '100vw', touchAction: 'none' }}
    >
      {/* Top bar: X Cancel | Title | Save check */}
      <div className="flex items-center justify-between px-4 pt-[max(env(safe-area-inset-top),10px)] pb-3 bg-[#111111] z-10 flex-shrink-0">
        <button onClick={onCancel} data-testid="crop-cancel" className="w-10 h-10 flex items-center justify-center active:scale-95">
          <X size={22} className="text-white/80" />
        </button>
        <p className="text-white text-sm font-semibold">
          {circular ? 'Adjust Photo' : 'Adjust Cover'}
        </p>
        <button
          onClick={handleSave}
          disabled={saving || !croppedAreaPixels}
          data-testid="crop-save"
          className="h-10 px-4 rounded-full bg-[#B6E600] text-[#111] text-sm font-extrabold active:scale-95 disabled:opacity-60 flex items-center justify-center"
        >
          {saving ? (
            <span className="w-4 h-4 border-2 border-[#111]/30 border-t-[#111] rounded-full animate-spin" />
          ) : (
            'Save'
          )}
        </button>
      </div>

      {/* Cropper area — full flex with touch-action none so gestures route to cropper */}
      <div className="flex-1 relative" style={{ touchAction: 'none' }}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={aspect}
          cropShape={circular ? 'round' : 'rect'}
          showGrid={!circular}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          minZoom={1}
          maxZoom={5}
          zoomSpeed={0.5}
          restrictPosition={true}
          objectFit={circular ? 'cover' : 'auto-cover'}
          style={{
            containerStyle: { background: '#111111', touchAction: 'none' },
            cropAreaStyle: {
              border: circular
                ? '3px solid rgba(182, 230, 0, 0.55)'
                : '2px solid rgba(182, 230, 0, 0.45)',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
            },
          }}
        />
      </div>

      {/* Zoom slider — always visible, safe-area bottom */}
      <div
        className="px-8 pt-4 pb-[max(env(safe-area-inset-bottom),14px)] bg-[#111111] flex-shrink-0"
        style={{ touchAction: 'auto' }}
      >
        <div className="flex items-center gap-3">
          <span className="text-white/55 text-xs font-semibold">−</span>
          <input
            type="range"
            min={1}
            max={5}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-[#B6E600] h-1"
            data-testid="crop-zoom"
            aria-label="Zoom"
          />
          <span className="text-white/55 text-xs font-semibold">+</span>
        </div>
        <p className="text-center text-white/45 text-[11px] mt-2 tracking-wide">
          Pinch to zoom · Drag to reposition
        </p>
      </div>
    </div>
  );
}
